import { Injectable } from '@angular/core';
import { VoiceSubscriptionOffering, VoiceSubscriptionType } from '../modules/subscription/models/offerings-response.model';
import { PcmProduct, PcmProductCategoryType, PcmProductPrice } from '../modules/subscription/models/pcm-product.model';
import { AssignedServiceProductModel } from '@app/shared/models/assigned-subscription-offering.model';

@Injectable({
  providedIn: 'root',
})
export class OfferingHelperService {
  static getVasProductFromOffering(offering: VoiceSubscriptionOffering): PcmProduct {
    return offering?.pcmProducts?.find(
      product => product.productCategory?.value === PcmProductCategoryType.ValueAddedService
    );
  }

  static getMobilePlanProductFromOffering(offering: VoiceSubscriptionOffering): PcmProduct {
    return offering?.pcmProducts?.find(product => product.productCategory?.value === PcmProductCategoryType.MobilePlan);
  }

  static sortOfferingsByMobilePlanPriceAsc(offerings: VoiceSubscriptionOffering[]): VoiceSubscriptionOffering[] {
    if (!offerings || offerings?.length < 1) return [];

    return [...offerings].sort((a, b) => {
      const aMobilePlan = OfferingHelperService.getMobilePlanProductFromOffering(a);
      const bMobilePlan = OfferingHelperService.getMobilePlanProductFromOffering(b);

      const aPrice = aMobilePlan?.monthlyPrice?.price.includingVAT ?? 0;
      const bPrice = bMobilePlan?.monthlyPrice?.price.includingVAT ?? 0;

      return aPrice - bPrice;
    });
  }

  static sortOfferingsByMobilePlanTypeAndDataVolumeAndPriceDesc(
    offerings: VoiceSubscriptionOffering[],
    sortByTypes: VoiceSubscriptionType[]
  ): VoiceSubscriptionOffering[] {
    if (!offerings || offerings?.length < 1) return [];

    return [...offerings].sort((a, b) => {
      const aDataVolume = a?.dataVolume ?? 0;
      const bDataVolume = b?.dataVolume ?? 0;

      const aMobilePlan = OfferingHelperService.getMobilePlanProductFromOffering(a);
      const bMobilePlan = OfferingHelperService.getMobilePlanProductFromOffering(b);

      const aPrice = aMobilePlan?.monthlyPrice?.price.includingVAT ?? 0;
      const bPrice = bMobilePlan?.monthlyPrice?.price.includingVAT ?? 0;

      if (sortByTypes?.length > 0) {
        const aType = sortByTypes.includes(a.subscriptionType);
        const bType = sortByTypes.includes(b.subscriptionType);

        if (aType && !bType) return -1;
        else if (!aType && bType) return 1;
      }

      if (bPrice === aPrice) return bDataVolume - aDataVolume;
      else return bPrice - aPrice;
    });
  }

  static getOfferingDisplayName(offering: VoiceSubscriptionOffering): string {
    const vasProduct = OfferingHelperService.getVasProductFromOffering(offering);
    const mobilePlanProduct = OfferingHelperService.getMobilePlanProductFromOffering(offering);

    return !!vasProduct && offering?.bindings?.length < 1 ? mobilePlanProduct?.productName : offering?.name;
  }

  static mapFromVasAddonToVasProduct(vasAddon: AssignedServiceProductModel): PcmProduct {
    if (!vasAddon) {
      return null;
    }

    const vasProduct = {
      pcmProductId: vasAddon.productDetails.productId,
      productName: vasAddon.productDetails.name,
      productCategory: vasAddon.productDetails.pcmProductCategory,
      labels: vasAddon.productDetails.labels,
      monthlyPrice: {
        price: vasAddon.priceSummary.price,
        discountPeriod: vasAddon.priceSummary.discountLength,
        isCustomerDiscount: false,
        isDiscounted: vasAddon.priceSummary.isDiscounted,
        originalPrice: vasAddon.priceSummary.originalPrice,
      } as PcmProductPrice,
      oneTimePrice: {} as PcmProductPrice,
    } as PcmProduct;

    return vasProduct;
  }
}
